import React, { useEffect } from 'react';
import { matchPath, Prompt } from 'react-router-dom';
import type { Action, Location } from 'history';
import { observer } from 'mobx-react';
import { FormState } from 'core/form';
import type BoardModel from 'app/stores/boards/BoardModel';
import type BoardPanelModel from 'app/stores/boards/BoardPanelModel';

export interface UnsavedChangesLocationState {
  ignoreUnsavedChanges?: boolean;
}

const BOARD_PATHS = ['/v4/ude/boards/:boardId', '/v4/library/boards/:boardId'];

function hasUnsavedFormEdits(panel?: BoardPanelModel, form?: FormState) {
  if (!panel || !form) {
    return false;
  }
  return panel.isNew ? form.dirty : panel.hasUnappliedQuery(form.getValues());
}

interface Props {
  board: BoardModel;
  panel?: BoardPanelModel;
  form?: FormState;
}

function UnsavedChangesPrompt({ board, panel, form }: Props) {
  const isBoardDirty = board.isDirty && !(board.isNew && board.panelCollection.size > 0);
  const when = board.canEdit && (hasUnsavedFormEdits(panel, form) || isBoardDirty);

  useEffect(() => {
    if (!when) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  const handleNavigation = (location: Location, action: Action) => {
    const isIgnored =
      action === 'PUSH' && (location.state as UnsavedChangesLocationState | undefined)?.ignoreUnsavedChanges;
    const isSameBoard =
      matchPath<{ boardId: string }>(location.pathname, BOARD_PATHS)?.params.boardId === String(board.id) &&
      !hasUnsavedFormEdits(panel, form);
    return (
      isIgnored ||
      isSameBoard ||
      'You have unsaved changes that may be lost if you leave.\n\nPress OK to leave or Cancel to stay.'
    );
  };

  return <Prompt when={when} message={handleNavigation} />;
}

export default observer(UnsavedChangesPrompt);
